import { Button } from '@mui/material'
import _ from 'lodash'
import { useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PartialDeep } from 'type-fest'

import { GapBox } from 'components/Box'
import { SectionHeader } from 'components/Header'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import { AbTestsAddEditContext } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { useGetAbTestVariantsSchema } from 'features/ab-tests/hooks/useGetAbTestVariantsSchema'
import { AbTestParametersSchema } from 'features/ab-tests/types/abTestParametersSchema'
import { generateSelector } from 'providers/product-fruits/utils'
import { DeepBoolean } from 'types/object'
import { VariantsTable } from '../components/VariantsTable'
import {
  extractFormValues,
  filterSchemaByParameters,
  filterSelectedParameters,
  filterVariantsByParameters,
  mergeFixedOptions,
  rearrangeTrafficShare,
  reassignDefaultReportedKeyValues,
  reassignFormValues,
  setVariants,
  variantsSortComparator,
} from '../utils'
import { VARIANTS_KEYS } from './constants'

import type { AbTestsCommonSchema, Variants, VariantsSchema } from '../abTestsCommonSchema'
import type { AbTestsCommonFormProps } from '../types'
import type { AbTestsAddEditContextType } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import type { AbTestVariantsSchema } from 'features/ab-tests/types/abTestVariantsSchema'

export const VariantsSection = ({ variant }: AbTestsCommonFormProps): JSX.Element => {
  const { t: tC } = useTranslation('common')
  const { t } = useTranslation('features/abTests', { keyPrefix: 'common' })

  const {
    abTest,
    isDisabled,
    loading: formLoading,
  } = useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)

  const {
    formState: { isSubmitted },
    getValues,
    setValue,
  } = useFormContext<AbTestsCommonSchema>()
  const { domainId, parameters, variants } = getValues()
  const [wasSubmitted, setWasSubmitted] = useState<boolean>(false)

  if (parameters === undefined) {
    throw Error(`Schema 'parameters' should be available.`)
  }
  if (variant === 'edit' && abTest === undefined) {
    throw Error(`AB test should be available for edit.`)
  }

  useEffect(() => {
    if (isSubmitted) {
      setWasSubmitted(true)
    }
  }, [isSubmitted])

  const { loading, schema } = useGetAbTestVariantsSchema({ domainId })
  const [partialSchema, setPartialSchema] = useState<
    PartialDeep<AbTestVariantsSchema> | undefined
  >()
  const [workspaceValues, setWorkspaceValues] = useState<PartialDeep<VariantsSchema> | undefined>()

  useEffect(() => {
    if (schema === undefined) return

    const filteredParameters: DeepBoolean<AbTestParametersSchema> =
      filterSelectedParameters(parameters)
    const filteredSchema: PartialDeep<AbTestVariantsSchema> = filterSchemaByParameters({
      filteredParameters,
      schema,
    })

    switch (variant) {
      case 'add': {
        if (_.isEmpty(filteredSchema)) {
          setPartialSchema(undefined)
          setValue('variants', undefined)
          break
        }

        const partialSchema: PartialDeep<AbTestVariantsSchema> = mergeFixedOptions(
          schema,
          filteredSchema,
        )

        setPartialSchema(partialSchema)
        setVariants({
          defaultFormValues: extractFormValues(partialSchema),
          onSetValue: setValue,
          variants,
        })
        setWorkspaceValues(extractFormValues(partialSchema, 'workspaceDefaultValue'))
        break
      }
      case 'edit': {
        if (abTest === undefined) {
          throw Error(`AB test should be available for edit.`)
        }

        let filteredByRemovedVariants: Variants = abTest.variants
        if (variants !== undefined) {
          filteredByRemovedVariants = Object.fromEntries(
            Object.entries(abTest.variants)
              .filter(([key]: [string, undefined | VariantsSchema]) => key in variants)
              .sort(variantsSortComparator),
          ) as Variants
        }
        const filteredVariants: Variants = filterVariantsByParameters({
          parameters,
          variants: filteredByRemovedVariants,
        })
        const partialSchema: PartialDeep<AbTestVariantsSchema> = mergeFixedOptions(
          schema,
          filteredSchema,
        )
        const defaults: Variants = reassignFormValues(
          filteredVariants,
          extractFormValues(wasSubmitted ? _.merge(partialSchema, variants) : partialSchema),
        )
        const merged = _.merge(
          {},
          filteredVariants,
          wasSubmitted ? _.merge(defaults, variants) : defaults,
        )

        setValue('variants', merged)
        setPartialSchema(partialSchema)
        setWorkspaceValues(extractFormValues(partialSchema, 'workspaceDefaultValue'))
        break
      }
    }
  }, [parameters, schema])

  if (schema === undefined || loading) {
    return <Loading />
  }

  const handleAddVariant = (variants: Variants): void => {
    const existingKeys = Object.keys(variants)
      .filter(key => key.startsWith('variant'))
      .sort()

    const nextVariant = VARIANTS_KEYS.find(letter => !existingKeys.includes(letter))

    if (nextVariant && partialSchema) {
      const formValues: Variants = {
        ...variants,
        [nextVariant]: extractFormValues(partialSchema),
      }

      setValue('variants', rearrangeTrafficShare(reassignDefaultReportedKeyValues(formValues)), {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  return (
    <Paper
      data-product-fruits={
        variant === 'add' ? generateSelector({ step: 9, subTour: 2, tour: 5 }) : undefined
      }
    >
      <SectionHeader
        subtitle={t('variants.subtitle')}
        title={t('variants.title')}
        titleEndAdornment={
          <Button
            data-cy='ab-tests-add-variant-button'
            data-product-fruits={
              variant === 'add' ? generateSelector({ step: 5, subTour: 2, tour: 5 }) : undefined
            }
            disabled={isDisabled || formLoading}
            onClick={(): void => (variants ? handleAddVariant(variants) : undefined)}
            variant='contained'
          >
            {tC('actions.addNew')}
          </Button>
        }
      />

      <GapBox />

      {variants && partialSchema && workspaceValues && (
        <VariantsTable
          schema={partialSchema}
          values={variants}
          variant={variant}
          workspaceValues={workspaceValues}
        />
      )}
    </Paper>
  )
}
